import { Link, useLocation } from 'react-router-dom'
import css from './shell.module.css'
import { useDepartments } from '../data/queries'
import { departmentsFrom } from '../data/map/department'

/**
 * Navigation in two groups. The first is the fleet itself -- what it is doing,
 * what it cost, what it decided -- and is a fixed list of views. The second is
 * one entry per department and is *data*: it comes from the `departments`
 * table through the same map the inbox's filters use, so a department that
 * comes online appears here with no code change (product rule 2). A hardcoded
 * list would die quietly, because a missing entry reads as a lane with nothing
 * to show.
 *
 * Reports and Runbook sit outside both. Reports is a shelf that will hold
 * several departments' reports at once, so it is nobody's department entry,
 * and it is not a view onto the fleet's own work either; Runbook is help.
 */
const FLEET = [
  // First, because it is the question the other seven are asked in service of:
  // it names what is broken or waiting, and each row links on to the view
  // that holds the detail.
  { to: '/status', label: 'Status' },
  { to: '/lanes', label: 'Lanes' },
  // Beside Lanes because it is the other half of the same question: what the
  // fleet is doing, and what it has finished that is waiting on a person to
  // merge it.
  { to: '/merge', label: 'Merge' },
  { to: '/inbox', label: 'Inbox' },
  { to: '/activity', label: 'Activity' },
  { to: '/costs', label: 'Costs' },
  { to: '/changelog', label: 'Changelog' },
  { to: '/policy', label: 'Policy' },
  { to: '/directory', label: 'Directory' },
]

/**
 * A department has its own page now rather than being the inbox with a filter
 * on it. The `?dept=` filter stays and is still how the inbox is narrowed --
 * the desk links to it -- but it is no longer what a department *is* in the
 * nav.
 */
const DESK = '/desk'

/**
 * The two that belong to neither group, in one block under them. Only the
 * first carries `navTopLevel`: the class is the gap that separates the block
 * from the Departments list above, and putting it on both would split the two
 * entries apart as well.
 */
const TOP_LEVEL = [
  { to: '/reports', label: 'Reports' },
  { to: '/runbook', label: 'Runbook' },
]

function itemClass(active: boolean) {
  return active ? `${css.navItem} ${css.navItemActive}` : css.navItem
}

export function SideNav() {
  const departments = useDepartments()
  const { pathname } = useLocation()

  /** A view and everything under it: /inbox/:id is still the inbox. */
  const under = (to: string) => pathname === to || pathname.startsWith(`${to}/`)

  // React Router's NavLink would match a path prefix, which is wrong for both
  // groups here: /desk/:slug sits under a /desk that is not a view, and two
  // desks would light together the day one slug is the stem of another. So
  // these are plain links and the active state is worked out here.
  return (
    <nav className={css.sideNav} aria-label="Views">
      <h2 className={css.navGroup}>Fleet</h2>
      {FLEET.map((item) => {
        const active = under(item.to)
        return (
          <Link
            key={item.to}
            to={item.to}
            className={itemClass(active)}
            aria-current={active ? 'page' : undefined}
          >
            {item.label}
          </Link>
        )
      })}

      <h2 className={css.navGroup}>Departments</h2>
      {/* Loading, empty and errored all render as the heading and nothing
          under it. A spinner would be motion this product does not have, and an
          error style here would be urgency design (product rule 1) about
          something no operator can fix from the nav. */}
      {departmentsFrom(departments.data ?? []).map((d) => {
        const to = `${DESK}/${encodeURIComponent(d.slug)}`
        const active = pathname === to
        return (
          <Link
            key={d.slug}
            to={to}
            className={itemClass(active)}
            aria-current={active ? 'page' : undefined}
          >
            {d.label}
          </Link>
        )
      })}

      {TOP_LEVEL.map((item, index) => {
        const active = under(item.to)
        return (
          <Link
            key={item.to}
            to={item.to}
            className={index === 0 ? `${itemClass(active)} ${css.navTopLevel}` : itemClass(active)}
            aria-current={active ? 'page' : undefined}
          >
            {item.label}
          </Link>
        )
      })}
    </nav>
  )
}
